/***********************/
/* Angular + external */
import {
  Component,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChange,
  Input,
  Output,
  forwardRef,
  ViewChild,
  EventEmitter,
  ElementRef,
  AfterViewInit,
} from "@angular/core";
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
/***********************/
/* BFX */
import * as CubiqTS from "shared/cubiq-model-multi-asset";
// models
import { BfxInputControlStateEnum } from "./models/bfx-interface-controls.enums";

export const CLIENT_INTENT_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => BfxClientIntentToggleComponent),
  multi: true,
};

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

@Component({
  selector: "bfx-client-intent-toggle",
  templateUrl: "./html/bfx-toggle-client-intent.html",
  providers: [CLIENT_INTENT_VALUE_ACCESSOR],
  standalone: false,
})
export class BfxClientIntentToggleComponent
  implements OnInit, OnDestroy, OnChanges, ControlValueAccessor
{
  @Input() inputId: string;

  @Input("label") input_label: string;

  @Input("disabled") input_disabled: boolean;

  //@Input('intent') input_intent: boolean | string;

  @Input("ccy") input_ccy: string; // just so that the intent value is updated on ccy change

  @ViewChild("inputField", { static: true }) inputField: ElementRef;

  onModelChange: Function = (value: any) => {};
  onModelTouched: Function = () => {};

  /* External Validation - not integrated in code */
  input_externalState: BfxInputControlStateEnum;

  @Input()
  get externalState() {
    return this.input_externalState;
  }

  set externalState(value: any) {
    this.input_externalState = value;
  }

  /***************************************/
  /* DATA */
  public config: any = {};

  public data: any = {
    label: "",
    value: null, // CubiqTS.OrderIntent
    disabled: false,
    intentValue: false, // boolean => true = 0 = buy, false = 1 = sell,
    buttonLabel: "",
    template: {
      buy: "",
      buyLabelKey: "interface_control.client_intent.buy",
      buyValue: CubiqTS.OrderIntent.BUY, // 0
      sell: "",
      sellLabelKey: "interface_control.client_intent.sell",
      sellValue: CubiqTS.OrderIntent.SELL, // 1
    },
  };

  public locked: boolean = false;

  /**********************************************************************/
  /* CONSTRUCTOR  + LIFE CYCLE HOOKS */

  constructor(
    public element: ElementRef,
    public translationService: TranslateService,
  ) {}

  ngOnInit() {
    this.initialize();
  }

  ngOnDestroy() {}

  ngOnChanges() {
    // changes: {[propKey: string]: SimpleChange}
    this.initialize();
  }

  /**********************************************************************/
  /* ControlValueAccessor */

  writeValue(value: any): void {
    if (
      value != null &&
      (value === CubiqTS.OrderIntent.BUY || value === CubiqTS.OrderIntent.SELL)
    ) {
      if (value === CubiqTS.OrderIntent.BUY) this.setClientIntentToggleState(false);
      else if (value === CubiqTS.OrderIntent.SELL) this.setClientIntentToggleState(true);
    } else {
      this.setClientIntentToggleState(false);
    }
    this.onModelChange(this.data.value);
  }

  registerOnChange(fn: Function): void {
    this.onModelChange = fn;
  }

  registerOnTouched(fn: Function): void {
    this.onModelTouched = fn;
  }

  public onChange(event: any): void {
    this.onModelChange(this.data.value);
  }

  /**********************************************************************/
  /* INITIALIZATION */
  protected initialize(): void {
    this.initializeLabelTranslation();
    // set label
    if (this.input_label) {
      this.data.label = this.input_label;
    }
    // set disabled
    if (this.input_disabled != null) {
      this.data.disabled = this.input_disabled;
    }

    /* 		if(this.input_intent){
			if(typeof this.input_intent === 'boolean'){
				this.setClientIntentToggleState( this.input_intent);
			}else if((typeof this.input_intent === 'string' && this.input_intent.toUpperCase() === 'BUY') || (typeof this.input_intent === 'number' && this.input_intent === CubiqTS.OrderIntent.BUY)){
				this.setClientIntentToggleState(false);
			}else if((typeof this.input_intent === 'string' && this.input_intent.toUpperCase() === 'SELL') || (typeof this.input_intent === 'number' && this.input_intent === CubiqTS.OrderIntent.SELL)){
				this.setClientIntentToggleState(true);
			}
		}else{//default
			this.setClientIntentToggleState(false);
		} */
    if (
      this.data.value != null &&
      (this.data.value === CubiqTS.OrderIntent.BUY || this.data.value === CubiqTS.OrderIntent.SELL)
    ) {
      if (this.data.value === CubiqTS.OrderIntent.BUY) this.setClientIntentToggleState(false);
      else if (this.data.value === CubiqTS.OrderIntent.SELL) this.setClientIntentToggleState(true);
    } else {
      this.data.value = CubiqTS.OrderIntent.BUY;
      this.setClientIntentToggleState(false);
    }
    //this.onChange(null);
  }

  protected initializeLabelTranslation(): void {
    this.data.template.buy = this.translationService.instant(this.data.template.buyLabelKey);
    this.data.template.sell = this.translationService.instant(this.data.template.sellLabelKey);
  }

  /**********************************************************************/
  /* PUBLIC METHODS */
  public handleIntentChange(nextState: boolean): void {
    this.setClientIntentToggleState(nextState);
    //this.onChange(null);
  }

  public resetInputControl() {
    this.initializeLabelTranslation();

    this.data.label = this.input_label != null && this.input_label !== "" ? this.input_label : "";
    this.data.value = CubiqTS.OrderIntent.BUY;
    this.data.disabled = false;
    this.data.intentValue = false; // false = 0 = buy, true = 1 = sell,
    this.data.buttonLabel = this.data.template.buy;

    this.initialize();
  }

  public lockInputControl(): void {
    this.locked = true;
  }

  public unlockInputControl(): void {
    this.locked = false;
  }

  public focusInput() {
    this.inputField.nativeElement.focus();
  }

  /**********************************************************************/
  /* PRIVATE METHODS */

  private setClientIntentToggleState(state: boolean): void {
    if (state) {
      // sell
      this.data.intentValue = true;
      this.data.value = this.data.template.sellValue;
      this.data.buttonLabel = this.data.template.sell;
    } else {
      // buy
      this.data.intentValue = false;
      this.data.value = this.data.template.buyValue;
      this.data.buttonLabel = this.data.template.buy;
    }
    this.onChange(null);
  }
}
